import { useMutation } from '@tanstack/react-query';
import { ArrowLeft, KeyRound, Loader2, MailCheck } from 'lucide-react';
import { useState } from 'react';
import { api } from '../lib/api';
import { SplitScreen } from '../components/SplitScreen';

/**
 * "I cannot get in."
 *
 * The confirmation is deliberately the same whether or not the address has an
 * account. Saying "no such user" here would turn this page into a way to work
 * out who works at GoSpeedNet — and since the addresses are
 * firstname@gospeednet.co.ke, a list of staff is a list of valid logins.
 *
 * That means an honest screen has to avoid implying delivery it cannot promise.
 * It says *if* the address has an account, which is true, rather than "check
 * your inbox", which would be a lie a third of the time.
 */
export function ForgotPasswordPage({ onBack }: { onBack: () => void }) {
  const [email, setEmail] = useState('');

  const ask = useMutation({
    mutationFn: () => api.forgotPassword(email.trim()),
  });

  if (ask.isSuccess) {
    return (
      <SplitScreen reversed>
        <div className="w-full max-w-md">
          <div className="card text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary-50">
              <MailCheck className="h-6 w-6 text-secondary-600" aria-hidden="true" />
            </div>
            <h2 className="font-heading text-xl">Check your email</h2>
            <p className="mt-2 text-sm text-muted-foreground">{ask.data?.message}</p>
            <p className="mt-4 text-sm text-muted-foreground">
              Nothing arrived? It may have gone to spam, or the address may not have an account
              here. Your admin can set you a password directly.
            </p>
            <button type="button" onClick={onBack} className="btn-secondary mt-5 w-full">
              Back to sign in
            </button>
          </div>
        </div>
      </SplitScreen>
    );
  }

  return (
    <SplitScreen reversed>
      <div className="w-full max-w-md">
        <div className="card">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50">
            <KeyRound className="h-6 w-6 text-primary-600" aria-hidden="true" />
          </div>

          <h2 className="font-heading text-xl">Forgotten your password?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Put in the address you sign in with and we will send a link to set a new one. The link
            works once and lasts an hour.
          </p>

          <form
            className="mt-5 space-y-4"
            onSubmit={(event) => {
              event.preventDefault();
              ask.mutate();
            }}
          >
            {ask.isError && (
              <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
                {(ask.error as Error).message}
              </p>
            )}

            <label className="block">
              <span className="mb-1 block text-sm font-medium">Your email</span>
              <input
                type="email"
                autoComplete="username"
                className="input-field"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@gospeednet.co.ke"
                required
              />
            </label>

            <button
              type="submit"
              className="btn-primary w-full"
              disabled={ask.isPending || !email.includes('@')}
            >
              {ask.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Send me a link
            </button>
          </form>

          <button
            type="button"
            onClick={onBack}
            className="mt-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Back to sign in
          </button>
        </div>
      </div>
    </SplitScreen>
  );
}
